import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDatabase, ref, push, serverTimestamp } from 'firebase/database';
import { sendAdminNewApplication, sendProviderApplicationReceived } from '../services/emailService';
import './ApplyPage.css';
import { FaCheckCircle, FaBriefcase } from 'react-icons/fa';

const ADMIN_NOTIFICATION_EMAIL = process.env.REACT_APP_ADMIN_NOTIFICATION_EMAIL || 'support@skillhives.co.in';

const ApplyPage = () => {
    const { currentUser } = useAuth();
    const [formData, setFormData] = useState({
        fullName: '',
        email: currentUser?.email || '',
        phone: '',
        serviceCategory: '',
        experience: '',
        hourlyRate: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        skills: '',
        certifications: '',
        availability: {
            weekdays: false,
            weekends: false,
            emergency: false
        },
        bio: '',
        whyJoin: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                availability: {
                    ...prev.availability,
                    [name]: checked
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            const db = getDatabase();
            const applicationsRef = ref(db, 'jobApplications');

            const applicationData = {
                ...formData,
                userId: currentUser?.uid || 'anonymous',
                status: 'pending',
                appliedAt: serverTimestamp(),
                reviewedAt: null,
                reviewedBy: null,
                adminNotes: ''
            };

            await push(applicationsRef, applicationData);

            try {
                await sendProviderApplicationReceived(formData.email, {
                    applicantName: formData.fullName,
                    fullName: formData.fullName,
                    serviceCategory: formData.serviceCategory,
                    city: formData.city,
                    experience: formData.experience,
                });
            } catch (emailError) {
                console.error('Applicant email failed:', emailError);
            }

            try {
                await sendAdminNewApplication(ADMIN_NOTIFICATION_EMAIL, {
                    applicantName: formData.fullName,
                    fullName: formData.fullName,
                    email: formData.email,
                    phone: formData.phone,
                    serviceCategory: formData.serviceCategory,
                    city: formData.city,
                });
            } catch (emailError) {
                console.error('Admin application email failed:', emailError);
            }

            setSubmitted(true);
            setFormData({
                fullName: '',
                email: currentUser?.email || '',
                phone: '',
                serviceCategory: '',
                experience: '',
                hourlyRate: '',
                address: '',
                city: '',
                state: '',
                zipCode: '',
                skills: '',
                certifications: '',
                availability: {
                    weekdays: false,
                    weekends: false,
                    emergency: false
                },
                bio: '',
                whyJoin: ''
            });
        } catch (err) {
            console.error('Error submitting application:', err);
            setError('Failed to submit application. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="apply-page">
                <div className="container">
                    <div className="success-message">
                        <FaCheckCircle className="success-icon" />
                        <h2>Application Submitted Successfully!</h2>
                        <p>Thank you for applying to become a service provider with SkillHive.</p>
                        <p>Our admin team will review your application and get back to you within 2-3 business days.</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => setSubmitted(false)}
                        >
                            Submit Another Application
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="apply-page">
            <div className="container">
                <div className="apply-header">
                    <FaBriefcase className="header-icon" />
                    <h1>Apply to Become a Professional</h1>
                    <p>Join our network of trusted service providers and grow your business</p>
                </div>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit} className="apply-form">
                    <section className="form-section">
                        <h3>Personal Information</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="fullName">Full Name *</label>
                                <input
                                    type="text"
                                    id="fullName"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter your full name"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="phone">Phone Number *</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                    placeholder="+1 234 567 8900"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email Address *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                placeholder="your@email.com"
                            />
                        </div>
                    </section>

                    <section className="form-section">
                        <h3>Service Details</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="serviceCategory">Service Category *</label>
                                <select
                                    id="serviceCategory"
                                    name="serviceCategory"
                                    value={formData.serviceCategory}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select a service</option>
                                    <option value="electrician">Electrician</option>
                                    <option value="plumber">Plumber</option>
                                    <option value="carpenter">Carpenter</option>
                                    <option value="painter">Painter</option>
                                    <option value="ac-repair">AC Repair</option>
                                    <option value="cleaning">Cleaning</option>
                                    <option value="glass-windows">Glass & Windows</option>
                                    <option value="home-renovation">Home Renovation</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="experience">Years of Experience *</label>
                                <input
                                    type="number"
                                    id="experience"
                                    name="experience"
                                    value={formData.experience}
                                    onChange={handleChange}
                                    required
                                    min="0"
                                    placeholder="Years"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="hourlyRate">Hourly Rate (₹) *</label>
                            <input
                                type="number"
                                id="hourlyRate"
                                name="hourlyRate"
                                value={formData.hourlyRate}
                                onChange={handleChange}
                                required
                                min="0"
                                placeholder="Your hourly rate"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="skills">Skills & Specializations *</label>
                            <textarea
                                id="skills"
                                name="skills"
                                value={formData.skills}
                                onChange={handleChange}
                                required
                                rows="3"
                                placeholder="List your key skills (e.g., Wiring, Panel Installation, LED Installation)"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="certifications">Certifications & Licenses</label>
                            <textarea
                                id="certifications"
                                name="certifications"
                                value={formData.certifications}
                                onChange={handleChange}
                                rows="2"
                                placeholder="List your certifications and licenses"
                            />
                        </div>
                    </section>

                    <section className="form-section">
                        <h3>Location</h3>
                        <div className="form-group">
                            <label htmlFor="address">Street Address *</label>
                            <input
                                type="text"
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                required
                                placeholder="Street address"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="city">City *</label>
                                <input
                                    type="text"
                                    id="city"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    required
                                    placeholder="City"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="state">State *</label>
                                <input
                                    type="text"
                                    id="state"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                    required
                                    placeholder="State"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="zipCode">ZIP Code *</label>
                                <input
                                    type="text"
                                    id="zipCode"
                                    name="zipCode"
                                    value={formData.zipCode}
                                    onChange={handleChange}
                                    required
                                    placeholder="ZIP"
                                />
                            </div>
                        </div>
                    </section>

                    <section className="form-section">
                        <h3>Availability</h3>
                        <div className="checkbox-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="weekdays"
                                    checked={formData.availability.weekdays}
                                    onChange={handleChange}
                                />
                                <span>Monday - Friday</span>
                            </label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="weekends"
                                    checked={formData.availability.weekends}
                                    onChange={handleChange}
                                />
                                <span>Weekends</span>
                            </label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="emergency"
                                    checked={formData.availability.emergency}
                                    onChange={handleChange}
                                />
                                <span>Emergency Services</span>
                            </label>
                        </div>
                    </section>

                    <section className="form-section">
                        <h3>Additional Information</h3>
                        <div className="form-group">
                            <label htmlFor="bio">Professional Bio *</label>
                            <textarea
                                id="bio"
                                name="bio"
                                value={formData.bio}
                                onChange={handleChange}
                                required
                                rows="4"
                                placeholder="Tell us about yourself and your experience"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="whyJoin">Why do you want to join SkillHive? *</label>
                            <textarea
                                id="whyJoin"
                                name="whyJoin"
                                value={formData.whyJoin}
                                onChange={handleChange}
                                required
                                rows="3"
                                placeholder="Tell us why you want to join our platform"
                            />
                        </div>
                    </section>

                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn btn-primary btn-lg"
                            disabled={submitting}
                        >
                            {submitting ? 'Submitting...' : 'Submit Application'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ApplyPage;
